import { Container, Graphics } from 'pixi.js';
import { dayPhase, daylight, type FireHazard, type InteractiveObject, type Weather } from '@duck/contracts';
import { isReducedMotion } from './reduced-motion';

/**
 * Procedural ambient atmosphere for the "Living World" (M4.2 — emotion: Joy). The whole office shares
 * ONE sky: the server delta's `timeOfDay`/`weather` to everyone and we render it in code (ADR-0010 —
 * MVP visuals are code-authored Pixi particles, never generated images). This layer makes the world
 * *breathe* — a warm dawn, a cool night, a soft rain, steam curling off the coffee machine — without
 * ever touching anything about people (purely atmospheric, never a metric, never surveillance).
 *
 * PERFORMANCE is a first-class gate. Everything here is CHEAP and does ZERO per-frame allocations:
 *   • every particle (rain drop, steam puff) is OBJECT-POOLED — created once in the constructor and
 *     reused forever; we never `new` anything in {@link update};
 *   • counts are HARD-CAPPED ({@link RAIN_DROPS});
 *   • tinting is a single Graphics whose `.tint`/`.alpha` we set (no per-frame redraw, no filters/blur
 *     on the hot path);
 *   • the lerp toward the target tint reuses scratch numbers, never temp objects.
 *
 * ACCESSIBILITY is the other gate. This whole layer is DECORATIVE and lives BELOW the ducks/labels/
 * badges in the scene graph, so day/night dimming and weather tint the WORLD/backdrop only — the
 * foreground (ducks, presence pills, name labels, the in-world prompt) and the DOM HUD stay fully
 * legible. Night is brightness-capped ({@link MIN_DAYLIGHT}) so contrast is always preserved. The
 * dual-coded (icon + TEXT) state read-out lives in the DOM HUD, not here. When the OS asks for
 * reduced motion we render a correct STATIC frame (no falling rain, no pulsing, no sway).
 */

/** Hard cap on simultaneous rain streaks (pooled, reused). Scattered across the whole world, so scaled up
 *  for the 5x castle envelope (3600x2680) to keep an on-screen drizzle density — still ONE batched draw call. */
const RAIN_DROPS = 700;
/** Steam puffs kept alive per coffee source (pooled). Small + soft — a tiny curl, not a smoke machine. */
const STEAM_PER_SOURCE = 6;

/**
 * Floor on how dark night may get, as a daylight value. `daylight()` reaches 0 at midnight; we never
 * let the world dim past this so the backdrop (and therefore relative contrast) stays readable. The
 * accessibility gate: dimming is mood, never a legibility tax.
 */
const MIN_DAYLIGHT = 0.4;

/** How fast the lighting tint eases toward its target per second (frame-rate independent, no alloc). */
const LIGHT_LERP_PER_SEC = 2.2;

/** Rain streak stroke — hoisted so the hot path passes ONE shared object, never a per-frame literal. */
const RAIN_STROKE = { color: 0xbcd4ff, width: 1.6, alpha: 0.45 };

/** Warm dawn/dusk wash vs. cool night wash — mixed by phase so the world *feels* the time of day. */
const WARM = { r: 1.0, g: 0.62, b: 0.32 }; // sunrise/sunset amber
const COOL = { r: 0.32, g: 0.42, b: 0.85 }; // deep-blue night

/**
 * How strongly the guild's WORLD-THEME sky colour (M8 "Guilds at Scale") tints the day/night wash. The
 * sky is composed ON TOP of the time-of-day warm/cool mix, so each world feels distinct at every hour
 * (a violet academy night, a cyan-void station, a warm-amber café) WITHOUT changing the day clock or
 * the {@link MIN_DAYLIGHT} contrast floor — theming the look, never the legibility (a11y).
 */
const SKY_THEME_BLEND = 0.4;

interface RainDrop {
  x: number;
  y: number;
  /** Fall speed (px/s); varied per drop for parallax-ish depth, fixed once at construction. */
  speed: number;
  len: number;
}

interface SteamPuff {
  gfx: Graphics;
  /** 0..1 life along the rising curl; staggered per puff so the column is continuous. */
  life: number;
  /** Per-puff drift so puffs don't rise in a single rigid line (fixed once). */
  drift: number;
  /** Source origin (the coffee spout) the puff resets to — fixed at construction (no per-frame alloc). */
  ox: number;
  oy: number;
}

export class AmbientLayer {
  /** Added by the engine between the office map and the ducks → tints backdrop, never the foreground. */
  readonly root = new Container();

  // ── Day/night lighting (one Graphics, animated via tint/alpha; never redrawn per frame). ──
  private readonly lightOverlay: Graphics;
  /** Eased, current light wash (mutated in place — no per-frame object allocation). */
  private readonly light = { r: 0.06, g: 0.09, b: 0.2, a: 0 };
  private readonly lightTarget = { r: 0.06, g: 0.09, b: 0.2, a: 0 };
  /**
   * The guild's WORLD-THEME sky colour as 0..1 channels (M8). Composed into the day/night wash so the
   * sky feels like the chosen world at every hour. Defaults to the office's blue; {@link setSkyTint}
   * swaps it (idempotent + cheap) when the theme changes — the next frame eases the wash toward it, so
   * an owner's theme switch re-skins the sky live without a pop. Never a per-frame allocation.
   */
  private readonly sky = { r: COOL.r, g: COOL.g, b: COOL.b };

  // ── Weather: pooled rain drop POSITIONS drawn into ONE Graphics (a single draw call for the whole
  //    drizzle — 140 separate Graphics would be ~140 draw calls and tank FPS) + a cloud dim. ──
  private readonly rainGfx = new Graphics();
  private readonly rain: RainDrop[] = [];
  private readonly cloudOverlay: Graphics;

  // ── Ambient life: pooled steam, swaying greenery, monitor glow. ──
  private readonly steamContainer = new Container();
  private readonly steam: SteamPuff[] = [];
  /** Decor that sways (gentle sin offset). Built once; only `.rotation` changes per frame. */
  private readonly sway: { gfx: Graphics; phase: number; amp: number }[] = [];
  /** Monitor glows pulse very subtly (alpha only). Static under reduced motion. */
  // x/y carry the glow's WORLD position: its geometry is baked at world coords (the gfx node itself stays
  // at 0,0), so the off-screen cull must read these, not gfx.x/gfx.y.
  private readonly glows: { gfx: Graphics; phase: number; x: number; y: number }[] = [];
  /**
   * Animated FIRE tongues over each open flame of the RESOLVED LEVEL (ADR-0052/0054) — the castle's hearth /
   * forge / mess fires LICK + flicker; the office passes an empty list, so nothing burns there (level-scoped,
   * never global). Pooled + built ONCE from the `fireHazards` handed to the constructor; {@link updateLife}
   * only mutates `.scale`/`.alpha` over the shared {@link clock} (zero per-frame allocation), holds a STATIC
   * lit frame under reduced motion, and is off-screen culled per flame via the same `isVisible` test the glows
   * use. `x/y` carry the hazard's WORLD centre for that cull; each flame's base sits at the node origin so a
   * vertical scale flickers it from the base.
   */
  private readonly fire: { gfx: Graphics; phase: number; x: number; y: number }[] = [];

  private weather: Weather = 'clear';
  /** Eased target for the overcast veil so weather changes wash in like the lighting (no instant pop). */
  private cloudTargetAlpha = 0;
  /** Whether the current rain frame has been drawn (lets reduced-motion draw the drizzle just once). */
  private rainDrawn = false;
  private time = 0;
  /** Monotonic seconds for ambient-life phases — advanced from dt, never `performance.now()` alloc. */
  private clock = 0;
  private firstFrame = true;
  // cost-census: the lighting/weather wash only changes on a real input change (timeOfDay / weather /
  // theme-sky). Track the last inputs + a "settled" flag so once the eased wash has converged on its
  // (currently engine-pinned-constant) target we skip the per-frame lighting/weather recompute entirely.
  private lastTime = -1;
  private lastWeather: Weather | null = null;
  private lightingSettled = false;

  /** The resolved level's world HEIGHT (Phase 3) — wraps the rain envelope (castle ≫ office). The width is
   *  consumed only at construction (the overlays are baked at world size), so it isn't retained. */
  private readonly worldHeight: number;

  /**
   * Build the ambient layer for the RESOLVED level (Phase 3): its interactive `objects` seed the per-prop
   * idle glows + coffee steam, and its `worldWidth/worldHeight` size the full-world lighting/cloud overlays
   * and the rain envelope (the castle is 3600x2680, not the office 1600x1200). Constructed once per level
   * by the engine after it resolves `levelId` (rebuilt on a level swap).
   */
  constructor(
    objects: readonly InteractiveObject[],
    worldWidth: number,
    worldHeight: number,
    fireHazards: readonly FireHazard[] = [],
  ) {
    this.worldHeight = worldHeight;
    const W = worldWidth;
    const H = worldHeight;

    // Full-world lighting wash. A plain white rect we TINT + fade; sits at the bottom of this layer so
    // it colours the floor/rooms beneath the ducks. We animate tint/alpha only (no geometry redraw).
    this.lightOverlay = new Graphics().rect(0, 0, W, H).fill(0xffffff);
    this.lightOverlay.tint = 0x101830;
    this.lightOverlay.alpha = 0;
    this.root.addChild(this.lightOverlay);

    // Cloud/overcast dim — a soft cool veil shown only for `cloudy`/`rain`. Same cheap tint+alpha trick.
    this.cloudOverlay = new Graphics().rect(0, 0, W, H).fill(0xffffff);
    this.cloudOverlay.tint = 0x2a3550;
    this.cloudOverlay.alpha = 0;
    this.root.addChild(this.cloudOverlay);

    // Interactive idle glow (Art Revision S5): a soft, slowly-breathing halo over each interactive's
    // screen/light so the prop reads as ALIVE and special BEFORE its amber interaction ring appears —
    // an inert decorative sofa stays quiet, a workstation/TV/coffee/arcade gently glows. Built ONCE;
    // the pulse is alpha-only (see updateLife) and steadies under reduced motion. Colours differ by kind
    // so each interactive has its own light signature, never a uniform wash.
    for (const o of objects) {
      let glow: Graphics | null = null;
      if (o.kind === 'desk' || o.kind === 'tv') {
        glow = new Graphics()
          .circle(o.x, o.y, 30)
          .fill({ color: 0x9fd0ff, alpha: 0.16 })
          .circle(o.x, o.y, 16)
          .fill({ color: 0xeaf6ff, alpha: 0.18 });
      } else if (o.kind === 'coffee') {
        // a small warm glow at the machine's lit display (body rises above the floor point)
        glow = new Graphics()
          .circle(o.x, o.y - 24, 12)
          .fill({ color: 0xffd9a0, alpha: 0.2 })
          .circle(o.x, o.y - 24, 6)
          .fill({ color: 0xfff0d6, alpha: 0.22 });
      } else if (o.kind === 'arcade') {
        // a neon marquee bloom at the top of the cabinet
        glow = new Graphics()
          .ellipse(o.x, o.y - 34, 20, 8)
          .fill({ color: 0xff7ad1, alpha: 0.16 })
          .ellipse(o.x, o.y - 34, 12, 5)
          .fill({ color: 0xeaf6ff, alpha: 0.16 });
      }
      if (!glow) continue;
      this.glows.push({ gfx: glow, phase: o.x * 0.01, x: o.x, y: o.y });
      this.root.addChild(glow);
    }

    // Steam: pooled puffs above every coffee source. Soft white circles that rise + fade + drift.
    for (const o of objects) {
      if (o.kind !== 'coffee') continue;
      const ox = o.x;
      const oy = o.y - 18; // just above the coffee spout
      for (let i = 0; i < STEAM_PER_SOURCE; i++) {
        const gfx = new Graphics().circle(0, 0, 6).fill({ color: 0xffffff, alpha: 0.5 });
        gfx.x = ox;
        gfx.y = oy;
        // Stagger initial life so the column is continuous from the first frame (no synchronized pop).
        this.steam.push({ gfx, life: i / STEAM_PER_SOURCE, drift: (i % 2 === 0 ? 1 : -1) * 6, ox, oy });
        this.steamContainer.addChild(gfx);
      }
    }
    this.root.addChild(this.steamContainer);

    // Greenery / decor sway: Castle Hearthhold (ADR-0051) seeds its potted greenery from the room
    // decorators (themed bay laurels / ferns / ivy), so the generic ambient shrubs are retired here —
    // the old floorplan-v2 dabs landed at arbitrary castle spots. (Left as a seam for future swaying decor.)
    const PLANT_SPOTS: [number, number][] = [];
    for (const [px, py] of PLANT_SPOTS) {
      const plant = new Graphics()
        .ellipse(0, -16, 12, 18)
        .fill(0x3f9d52)
        .ellipse(-8, -8, 9, 13)
        .fill(0x4fae5f)
        .ellipse(8, -8, 9, 13)
        .fill(0x357f44)
        .rect(-3, -2, 6, 8)
        .fill(0x6b4a2b);
      plant.x = px;
      plant.y = py;
      this.sway.push({ gfx: plant, phase: px * 0.013 + py * 0.007, amp: 0.06 });
      this.root.addChild(plant);
    }

    // Animated FIRE (ADR-0052/0054): a small cluster of flame tongues over each open flame of the RESOLVED
    // LEVEL (`fireHazards` — castle has 3, office has none), built ONCE here and only flickered (scale/alpha)
    // in updateLife. Each flame is a teardrop drawn with its base at the node origin (tip up) so a vertical
    // scale licks it from the base. Centre flames are tallest; each carries its own phase so they don't pulse
    // in lockstep. Tiny pool (~15 across the castle's fires) — cheaper than one extra duck; ZERO on the office.
    const teardropFlame = (hw: number, fh: number): Graphics =>
      new Graphics()
        .moveTo(0, 0)
        .bezierCurveTo(hw, -fh * 0.3, hw * 0.7, -fh * 0.8, 0, -fh)
        .bezierCurveTo(-hw * 0.7, -fh * 0.8, -hw, -fh * 0.3, 0, 0)
        .fill(0xff7a2a)
        .moveTo(0, -fh * 0.05)
        .bezierCurveTo(hw * 0.6, -fh * 0.3, hw * 0.45, -fh * 0.62, 0, -fh * 0.78)
        .bezierCurveTo(-hw * 0.45, -fh * 0.62, -hw * 0.6, -fh * 0.3, 0, -fh * 0.05)
        .fill(0xffc24a)
        .moveTo(0, -fh * 0.1)
        .bezierCurveTo(hw * 0.3, -fh * 0.28, hw * 0.22, -fh * 0.5, 0, -fh * 0.6)
        .bezierCurveTo(-hw * 0.22, -fh * 0.5, -hw * 0.3, -fh * 0.28, 0, -fh * 0.1)
        .fill(0xfff0b8);
    for (const h of fireHazards) {
      const count = h.r >= 50 ? 6 : h.r >= 36 ? 5 : 4;
      const bedW = h.r * 0.9; // the fire-bed width the flames rise across
      const maxH = h.r * 0.95; // tallest (centre) flame height
      const hw = Math.max(5, h.r * 0.22); // flame half-width
      for (let i = 0; i < count; i++) {
        const t = i / (count - 1); // 0..1 across the fire bed (count is always ≥ 4)
        const edge = Math.abs(t - 0.5) * 2; // 0 at centre … 1 at the edges
        const flame = teardropFlame(hw, maxH * (1 - edge * 0.45)); // centre flames tallest
        flame.x = h.x + (t - 0.5) * bedW;
        flame.y = h.y + h.r * 0.25; // base sits in the lower part of the fire pool
        this.fire.push({ gfx: flame, phase: i * 1.3 + h.x * 0.01, x: h.x, y: h.y });
        this.root.addChild(flame);
      }
    }

    // Rain: pooled drop POSITIONS only. We draw all streaks into ONE Graphics each rainy frame (one
    // stroke → one draw call for the whole drizzle). Subtle blue-white, low alpha so it never competes
    // with foreground contrast (and the layer sits below the ducks).
    for (let i = 0; i < RAIN_DROPS; i++) {
      const len = 10 + (i % 5) * 3;
      // golden-angle scatter → even spread without Math.random per-create.
      this.rain.push({ x: (i * 137.5) % W, y: (i * 263.1) % H, speed: 620 + (i % 7) * 55, len });
    }
    this.rainGfx.visible = false;
    this.root.addChild(this.rainGfx);
  }

  /**
   * Drive the whole layer from the shared room ambient + the frame delta. Pure tint/position math —
   * no allocations. `timeOfDay` and `weather` come straight off the authoritative room state, so every
   * client renders the same sky. Reduced-motion → snaps lighting to target and freezes all motion.
   * `isVisible(wx,wy)` (optional) lets the engine cull off-screen ambient life — see {@link updateLife}.
   */
  update(
    timeOfDay: number,
    weather: Weather,
    dtMs: number,
    isVisible?: (wx: number, wy: number) => boolean,
  ): void {
    this.time = timeOfDay;
    const dt = Math.min(dtMs, 50) / 1000;
    this.clock += dt;

    // cost-census: a real input change re-arms the eased wash; otherwise, once it has converged on its
    // (unchanging) target, skip the per-frame lighting/weather math — it would produce an identical tint.
    if (timeOfDay !== this.lastTime || weather !== this.lastWeather) {
      this.lastTime = timeOfDay;
      this.lastWeather = weather;
      this.lightingSettled = false;
    }
    if (!this.lightingSettled) {
      this.updateLighting(timeOfDay, dt);
      this.updateWeather(weather, dt);
      this.lightingSettled = this.washConverged(weather);
    }
    this.updateLife(dt, isVisible);
    this.firstFrame = false;
  }

  /** True when the eased day/night + cloud wash has reached its target, so further recompute is a no-op.
   *  Rain animates every frame, so it is never "settled" while it rains. (cost-census.) */
  private washConverged(weather: Weather): boolean {
    if (weather === 'rain') return false;
    const eps = 0.002; // < 0.5/255 — below one tint quantum, i.e. visually converged
    return (
      Math.abs(this.light.r - this.lightTarget.r) < eps &&
      Math.abs(this.light.g - this.lightTarget.g) < eps &&
      Math.abs(this.light.b - this.lightTarget.b) < eps &&
      Math.abs(this.light.a - this.lightTarget.a) < eps &&
      Math.abs(this.cloudOverlay.alpha - this.cloudTargetAlpha) < eps
    );
  }

  /** Day-of phase label for the dual-coded HUD indicator/observability (delegates to the contract). */
  phase(): ReturnType<typeof dayPhase> {
    return dayPhase(this.time);
  }

  /**
   * Re-skin the sky to the guild's WORLD-THEME colour (M8 "Guilds at Scale"). Called by the engine on a
   * real theme change (idempotent + cheap — just unpacks one packed 0xRRGGBB into 0..1 channels); the
   * next {@link update} eases the day/night wash toward it, so the sky changes smoothly (no pop). The
   * accessibility contract is unchanged: this only colours the decorative backdrop below the ducks and
   * the {@link MIN_DAYLIGHT} contrast floor still applies, so legibility is never themed away.
   */
  setSkyTint(skyHex: number): void {
    this.sky.r = ((skyHex >> 16) & 0xff) / 255;
    this.sky.g = ((skyHex >> 8) & 0xff) / 255;
    this.sky.b = (skyHex & 0xff) / 255;
    this.lightingSettled = false; // cost-census: a theme re-skin moves the wash target → re-arm the ease.
  }

  // ── Lighting ──────────────────────────────────────────────────────────────────────────────────

  private updateLighting(t: number, dt: number): void {
    // Shared brightness curve (matches the server's clock). Capped so night never kills contrast.
    const rawLight = daylight(t);
    const light = MIN_DAYLIGHT + (1 - MIN_DAYLIGHT) * rawLight;
    const phase = dayPhase(t);

    // Warmth: dawn/dusk lean amber, night leans deep-blue, midday is near-neutral. We blend a single
    // wash colour + alpha; brighter daylight → lower overlay alpha (the floor shows through).
    // dimAmount in 0..1: how strong the wash is right now.
    const dimAmount = 1 - light; // bright day → ~0; capped night → 1 - MIN_DAYLIGHT.
    let warmth: number; // 0 = cool/night wash, 1 = warm dawn/dusk wash
    if (phase === 'night') warmth = 0;
    else if (phase === 'dawn' || phase === 'dusk') warmth = 1;
    else warmth = 0.35; // day: a faint warm neutrality

    // Target wash colour (lerp warm↔cool) and a max alpha scaled by how dim it is.
    let tr = COOL.r + (WARM.r - COOL.r) * warmth;
    let tg = COOL.g + (WARM.g - COOL.g) * warmth;
    let tb = COOL.b + (WARM.b - COOL.b) * warmth;
    // M8: compose the guild's THEME sky over the time-of-day wash so each world feels distinct at every
    // hour. Bias toward the sky MOST at night (when the wash is strongest/coolest) and least at warm
    // dawn/dusk (so a sunrise still reads as a sunrise) — i.e. weight by how cool/dim the moment is.
    const skyWeight = SKY_THEME_BLEND * (1 - warmth);
    tr += (this.sky.r - tr) * skyWeight;
    tg += (this.sky.g - tg) * skyWeight;
    tb += (this.sky.b - tb) * skyWeight;
    this.lightTarget.r = tr;
    this.lightTarget.g = tg;
    this.lightTarget.b = tb;
    this.lightTarget.a = dimAmount * 0.55; // cap the wash so the world never goes muddy.

    // Ease toward the target (snap on the first frame and under reduced-motion → always correct).
    const k = isReducedMotion() || this.firstFrame ? 1 : Math.min(1, LIGHT_LERP_PER_SEC * dt);
    this.light.r += (this.lightTarget.r - this.light.r) * k;
    this.light.g += (this.lightTarget.g - this.light.g) * k;
    this.light.b += (this.lightTarget.b - this.light.b) * k;
    this.light.a += (this.lightTarget.a - this.light.a) * k;

    // Apply via tint (0..255 per channel packed into a hex) + alpha. No geometry redraw.
    const r = Math.round(clamp01(this.light.r) * 255);
    const g = Math.round(clamp01(this.light.g) * 255);
    const b = Math.round(clamp01(this.light.b) * 255);
    this.lightOverlay.tint = (r << 16) | (g << 8) | b;
    this.lightOverlay.alpha = clamp01(this.light.a);
  }

  // ── Weather ───────────────────────────────────────────────────────────────────────────────────

  private updateWeather(weather: Weather, dt: number): void {
    if (weather !== this.weather) {
      this.weather = weather;
      this.rainGfx.visible = weather === 'rain';
      if (weather === 'rain') this.rainDrawn = false; // force one (re)draw when rain (re)starts
    }

    // Overcast veil for cloudy AND rain (a touch of grey). Ease toward the target with the SAME
    // frame-rate-independent factor as the lighting so the veil washes in/out instead of popping
    // (snap on the first frame and under reduced-motion → always correct). Scalar lerp, no alloc.
    this.cloudTargetAlpha = weather === 'clear' ? 0 : weather === 'cloudy' ? 0.14 : 0.1;
    const ck = isReducedMotion() || this.firstFrame ? 1 : Math.min(1, LIGHT_LERP_PER_SEC * dt);
    this.cloudOverlay.alpha += (this.cloudTargetAlpha - this.cloudOverlay.alpha) * ck;

    if (this.weather !== 'rain') return;

    // Reduced motion → static drizzle: draw the streaks ONCE, then skip (no per-frame work at all).
    if (isReducedMotion()) {
      if (this.rainDrawn) return;
    } else {
      const H = this.worldHeight;
      for (const d of this.rain) {
        d.y += d.speed * dt;
        if (d.y > H) d.y -= H + d.len; // wrap to top (pooled position, no allocation)
      }
    }

    // Redraw ALL streaks into the single Graphics in ONE stroke → one draw call for the whole rain.
    this.rainGfx.clear();
    for (const d of this.rain) this.rainGfx.moveTo(d.x, d.y).lineTo(d.x + 1.4, d.y + d.len);
    this.rainGfx.stroke(RAIN_STROKE);
    this.rainDrawn = true;
  }

  // ── Ambient life ──────────────────────────────────────────────────────────────────────────────

  private updateLife(dt: number, isVisible?: (wx: number, wy: number) => boolean): void {
    // cost-census: animating a source the camera can't see only burns Math and (worse) re-dirties a Pixi
    // transform that is then re-processed every render. The office shows ~1 of 12 rooms, so most ambient
    // life is off-screen — skip any source outside the viewport (+ the engine's 140px no-pop margin, so a
    // source is already animating by the time it scrolls into view). A frozen source keeps its phase and
    // resumes seamlessly. Reduced-motion's one-time static setup is NOT culled — it must run once for all.
    // Steam: each puff rises, drifts, and fades; on death it resets to its source (pooled, no alloc).
    // Only when NOT reduced-motion; otherwise a single static puff is shown per source.
    if (isReducedMotion()) {
      if (this.firstFrame) {
        for (const p of this.steam) {
          p.gfx.x = p.ox;
          p.gfx.y = p.oy - 10;
          p.gfx.alpha = 0.35;
          p.gfx.scale.set(1);
        }
      }
    } else {
      for (const p of this.steam) {
        if (isVisible && !isVisible(p.ox, p.oy)) continue; // off-screen → freeze (phase preserved)
        p.life += dt * 0.45;
        if (p.life >= 1) p.life -= 1;
        const rise = p.life * 34; // px the puff climbs over its life
        p.gfx.x = p.ox + Math.sin(p.life * Math.PI * 2) * p.drift;
        p.gfx.y = p.oy - rise;
        // Fade in then out; small scale-up as it climbs. alpha/scale only → cheap.
        p.gfx.alpha = Math.sin(p.life * Math.PI) * 0.5;
        p.gfx.scale.set(0.7 + p.life * 0.8);
      }
    }

    // Greenery sway: a gentle lean. Static (upright) under reduced motion.
    if (!isReducedMotion()) {
      for (const s of this.sway) {
        if (isVisible && !isVisible(s.gfx.x, s.gfx.y)) continue; // off-screen → hold the current lean
        s.gfx.rotation = Math.sin(this.clock * 0.9 + s.phase) * s.amp;
      }
    } else if (this.firstFrame) {
      for (const s of this.sway) s.gfx.rotation = 0;
    }

    // Monitor glow: a very subtle breathing pulse (alpha only). Steady under reduced motion.
    if (!isReducedMotion()) {
      for (const gl of this.glows) {
        if (isVisible && !isVisible(gl.x, gl.y)) continue; // off-screen → hold the current glow (world x/y)
        gl.gfx.alpha = 0.8 + Math.sin(this.clock * 1.4 + gl.phase) * 0.2;
      }
    } else if (this.firstFrame) {
      for (const gl of this.glows) gl.gfx.alpha = 0.85;
    }

    // Animated FIRE (ADR-0052/0054): the level's open flames LICK + flicker — a fast two-octave wobble of
    // height (scale.y) + alpha over the shared clock, each flame on its own phase. Off-screen culled per flame
    // (the forge + mess fires sit in side rooms, so they cost nothing while you're elsewhere; the office has
    // none at all). A STATIC lit frame under reduced motion. Scale/alpha only — no redraw, no allocation.
    if (!isReducedMotion()) {
      for (const fl of this.fire) {
        if (isVisible && !isVisible(fl.x, fl.y)) continue; // off-screen → hold (phase preserved)
        const s =
          0.84 + Math.sin(this.clock * 9 + fl.phase) * 0.16 + Math.sin(this.clock * 15 + fl.phase * 1.7) * 0.06;
        fl.gfx.scale.set(0.95 + (s - 1) * 0.3, s); // mostly a vertical lick, slight width breathe
        fl.gfx.alpha = 0.82 + Math.sin(this.clock * 11 + fl.phase * 1.3) * 0.16;
      }
    } else if (this.firstFrame) {
      for (const fl of this.fire) {
        fl.gfx.scale.set(1);
        fl.gfx.alpha = 0.9;
      }
    }
  }

  /**
   * Release pooled references. The engine destroys the Pixi display tree (`app.destroy(..., {children:
   * true})`) which already tears down `root` and every child, so we only drop our own references to
   * the pools here (matching how the engine clears its duck/burst pools) — never double-destroy nodes.
   */
  destroy(): void {
    this.rain.length = 0;
    this.steam.length = 0;
    this.sway.length = 0;
    this.glows.length = 0;
    this.fire.length = 0;
  }
}

/** Clamp to [0,1]. Inlined-friendly, no allocation. */
function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
